import { Component, computed, effect, ElementRef, inject, signal, viewChild } from '@angular/core';

import { PromptLibraryListComponent } from './prompt-library-list/prompt-library-list.component';
import { PromptLibraryService } from '../services/prompt-library.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { IInstructions } from '../../automation/interfaces/i-instructions';
import { IWorkflow } from '../../automation/interfaces/i-workflow';
import { MatExpansionModule } from '@angular/material/expansion';
import { ChatSessionService } from '../services/chat-session.service';
import { ISensitivityLabel } from '../../../interfaces/i-sensitivity-label';
import { ILibraryItem } from './interfaces/i-library-item';
import { MultiSessionService } from '../../../services/multi-session.service';


/**
 * Component that houses the Automation Library drawer
 */
@Component({
  selector: 'app-prompt-library',
  imports: [
    MatIcon,
    MatButtonModule,
    MatTooltipModule,
    MatInputModule,
    MatExpansionModule,
    FormsModule,
    PromptLibraryListComponent
],
  templateUrl: './prompt-library.component.html',
  styleUrls: ['./prompt-library.component.scss']
})
export class PromptLibraryComponent {
  tooltipText = 'To add a Prompt, you can either create a new Instruction or Workflow, or click on the Save icon in any User sent prompt in the chat session. To run an Automation, click on the Automation name.';
  // Material Angular Expansion Panel
  accordion = viewChild.required<ElementRef>('accordion');

  // Search term for filtering prompts
  searchTerm = '';

  // Filtered data for the Automation Library
  instructions = signal<ILibraryItem[]>([]);
  workflows = signal<ILibraryItem[]>([]);

  // Original data from the service
  #originalInstructions: IInstructions[] = [];
  #originalWorkflows: IWorkflow[] = [];
  #promptLibraryService = inject(PromptLibraryService);
  #chatSessionService = inject(ChatSessionService);
  #multiSessionService = inject(MultiSessionService);
  
  // Computed signal for global automation state
  isAutomationActive = computed(() => 
    this.#multiSessionService.hasReachedMaxConcurrency() || this.#multiSessionService.isSessionStreaming(this.#chatSessionService.selectedSessionId())
  );

  constructor() {
    this.#promptLibraryService.loadWorkflows();
    this.#promptLibraryService.loadInstructions();
    // Use effect to reactively update data when the service data changes
    effect(() => {
      // Update the original data sources whenever they change in the service
      this.#originalInstructions = this.#promptLibraryService.instructions();
      this.#originalWorkflows = this.#promptLibraryService.workflows();

      // Always re-filter when data changes to apply both sensitivity and search filters
      this.filterPrompts();
    });

    // React to changes in selected session
    effect(() => {
      // If the selected session changes, re-filter prompts based on sensitivity labels
      if (this.#chatSessionService.selectedSession()) {
        this.filterPrompts();
      }
    });
  }

  /**
   * Filter prompts based on search term and sensitivity labels from the current session
   * This implementation filters by name, description, and sensitivity labels in both instructions and workflows
   * It also marks items as disabled if they don't have matching sensitivity labels and sorts them accordingly
   */
  filterPrompts(): void {
    // Get current session sensitivity labels
    const sessionSensitivityLabels = this.#chatSessionService.selectedSession()?.sensitivityLabels || [];

    // Create ILibraryItem arrays from original data with sensitivity check
    const instructionItems = this.#createLibraryItems(
      this.#originalInstructions,
      sessionSensitivityLabels,
      'instruction'
    );

    const workflowItems = this.#createLibraryItems(
      this.#originalWorkflows,
      sessionSensitivityLabels,
      'workflow'
    );

    // If no search term, use all data with appropriate disabled flags
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.instructions.set(instructionItems);
      this.workflows.set(workflowItems);
      return;
    }

    // If there's a search term, apply text filtering 
    const term = this.searchTerm.toLowerCase().trim();

    // Filter instructions by text
    const filteredInstructions = instructionItems.filter(libItem => {
      const instruction = libItem.item as IInstructions;
      return instruction.details.name.toLowerCase().includes(term) ||
        instruction.details.prompt.toLowerCase().includes(term) ||
        instruction.details.description?.toLowerCase().includes(term);
    });

    // Filter workflows by text
    const filteredWorkflows = workflowItems.filter(libItem => {
      const workflow = libItem.item as IWorkflow;
      return workflow.details.name.toLowerCase().includes(term) ||
        workflow.details.description?.toLowerCase().includes(term);
    });

    // Update the signals
    this.instructions.set(filteredInstructions);
    this.workflows.set(filteredWorkflows);
  }

  /**
   * Creates library items with disabled state based on sensitivity label matching
   * @private
   * @param items Items to process (instructions or workflows)
   * @param sessionLabels Current session's sensitivity labels
   * @param type Type of item ('instruction' or 'workflow')
   * @returns Array of ILibraryItem with appropriate disabled flags, sorted with enabled items first
   */
  #createLibraryItems<T extends IInstructions | IWorkflow>(
    items: T[],
    sessionLabels: ISensitivityLabel[],
    type: 'instruction' | 'workflow'
  ): ILibraryItem[] {
    // Map items to ILibraryItem with disabled state
    const libraryItems = items.map(item => {
      // Get item's sensitivity labels
      const itemLabels = item.details.sensitivityLabels || [];
      
      // Item is disabled if it has no matching sensitivity labels with the session
      // If sessionLabels is empty, all items are enabled (nothing is disabled)
      const isDisabled = sessionLabels.length > 0 && !this.#hasSensitivityLabelMatch(itemLabels, sessionLabels);

      return {
        item,
        isDisabled,
        type
      };
    });

    // Sort: enabled items first, then disabled items
    return libraryItems.sort((a, b) => {
      // First sort by disabled state
      if (a.isDisabled !== b.isDisabled) {
        return a.isDisabled ? 1 : -1; // enabled items first
      }
      
      // If both have the same disabled state, sort alphabetically by name
      const nameA = a.item.details.name.toLowerCase();
      const nameB = b.item.details.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  /**
   * Check if any of item's labels match any of the session's labels
   * @private
   * @param itemLabels Item's sensitivity labels
   * @param sessionLabels Current session's sensitivity labels
   * @returns True if there's at least one match, false otherwise
   */
  #hasSensitivityLabelMatch(
    itemLabels: ISensitivityLabel[],
    sessionLabels: ISensitivityLabel[]
  ): boolean {
    // If the item has no sensitivity labels, it can't match
    if (!itemLabels.length) {
      return false;
    }

    // Check if any of the item's labels match any of the session's labels
    return itemLabels.some(itemLabel =>
      sessionLabels.some(sessionLabel =>
        itemLabel.id === sessionLabel.id || itemLabel.guid === sessionLabel.guid
      )
    );
  }

  /**
   * Clear the search term and reapply filtering based only on sensitivity labels
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.filterPrompts();
  }
}
